/**
 * The transcript drawn off `transcriptTurns` - the same conversation as the
 * plain text, but with the second each line was spoken.
 *
 * Two things this list can do that `parseTranscript` cannot, and both come from
 * the column rather than from the drawing:
 *
 *  - **The speakers are known, not guessed.** The text reader has to find
 *    "Name:" prefixes and drops every label when only one name appears, because
 *    one name repeated is a guess rather than a separation. These turns carry a
 *    ROLE the transcriber assigned, so a monologue stays attributed and a
 *    two-sided call cannot be collapsed by a rewritten prefix.
 *  - **Each line points into the audio.** That is the whole reason the column
 *    exists: reviewing a ten minute call through a wall of prose meant dragging
 *    the waveform and guessing.
 *
 * ⚠ MOST CALLS DO NOT REACH THIS FILE. Only ElevenLabs Scribe times its words;
 * Gemini is still the default transcriber and stores null on purpose rather than
 * inventing a number. `CallTranscript` falls back to the text reader, which is
 * the ordinary path, not the degraded one.
 */
import { Link2, Play } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { Avatar, fmtDuration } from "../../ui";
import type { TimedTurn } from "./transcript";

export function CallTimedTurns({
  turns,
  onSeek,
}: {
  turns: TimedTurn[];
  /**
   * Put the recording at this second, or absent when there is nothing to steer.
   *
   * Absent is a real state rather than a precaution: the audio of a single call
   * can be erased on its own from the call menu, and retention deletes audio on
   * a shorter clock than transcripts, so a timed transcript outliving its
   * recording is the designed outcome of two different features. The times
   * still say how the call was paced, so they are printed either way - as a
   * button when it can act and as plain text when it cannot. A control that
   * does nothing reads as broken, which is worse than no control.
   */
  onSeek?: (seconds: number) => void;
}) {
  const t = useT();
  // The whole list, not each turn: a call where the transcriber could not tell
  // the sides apart says so once, at the top, the way the text reader does.
  // Saying it per line would repeat one fact down the length of a conversation.
  const anonymous = turns.every((turn) => turn.speaker === null);

  return (
    <>
      {anonymous ? (
        <p className="mb-3 text-[11px] text-muted-foreground">{t("calls.transcript.unattributed")}</p>
      ) : null}

      {turns.map((turn, i) => {
        const who = turn.speaker
          ? t(
              turn.speaker === "operator"
                ? "calls.transcript.speakerOperator"
                : "calls.transcript.speakerClient",
            )
          : null;
        const stamp = fmtDuration(turn.at);
        return (
          <div key={i} className="crm-turn">
            {who ? (
              // The same two tones the live co-pilot's turns use, keyed off the
              // role rather than off the position in a list of names: the two
              // readings of one call must not colour its sides differently.
              <Avatar name={who} size={30} tone={turn.speaker === "operator" ? "soft" : "brand"} />
            ) : (
              <Link2 className="mt-1 size-4 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0 flex-1">
              <span className="crm-turn-h">
                {who ? <span className="crm-turn-n">{who}</span> : null}
                {onSeek ? (
                  <button
                    type="button"
                    className="crm-turn-at"
                    onClick={() => onSeek(turn.at)}
                    title={t("calls.transcript.seekTo", { time: stamp })}
                  >
                    <Play className="size-2.5 fill-current" />
                    {stamp}
                  </button>
                ) : (
                  <span className="crm-turn-at is-flat">{stamp}</span>
                )}
              </span>
              <span className="crm-turn-p block">{turn.text}</span>
            </span>
          </div>
        );
      })}
    </>
  );
}
